import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { withSpacing, clsx } from '@wds/utils';
import { createUseStyles, useTheme } from 'react-jss';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var objectWithoutPropertiesLoose = _objectWithoutPropertiesLoose;

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var objectWithoutProperties = _objectWithoutProperties;

const SEMANTIC_COLORS = Object.freeze(['primary', 'success', 'info', 'warning', 'upgrade', 'secondary', 'muted']);
const TYPE_COLORS = Object.freeze(['dark', 'light', 'darkMuted', 'lightMuted', 'link']);
const SIZES = Object.freeze(['lg', 'md', 'sm']);
Object.freeze(['primary', 'upgrade', 'info', 'success', 'warning', 'secondary']);
Object.freeze([...SIZES, 'xs']);
Object.freeze(['md', 'sm']);
Object.freeze(['success', 'warning']);
Object.freeze(['success', 'warning']);
Object.freeze(['data-one', 'data-two', 'data-three', 'data-four', 'data-five', 'data-six', 'data-seven', 'data-eight', 'data-nine', 'data-ten']);
Object.freeze({
  DOWN: 'ArrowDown',
  END: 'End',
  ESC: 'Escape',
  HOME: 'Home',
  LEFT: 'ArrowLeft',
  PAGE_DOWN: 'PageDown',
  PAGE_UP: 'PageUp',
  RIGHT: 'ArrowRight',
  UP: 'ArrowUp',
  ENTER: 'Enter',
  SPACE: ' ',
  BACKSPACE: 'Backspace',
  TAB: 'Tab'
}); // Deprecated. Moving to e.key

Object.freeze({
  DOWN: 40,
  END: 35,
  ESC: 27,
  HOME: 36,
  LEFT: 37,
  PAGE_DOWN: 34,
  PAGE_UP: 33,
  RIGHT: 39,
  UP: 38,
  ENTER: 13,
  SPACE: 32,
  BACKSPACE: 8,
  TAB: 9
});
Object.freeze({
  37: -1,
  38: -1,
  39: 1,
  40: 1
});
Object.freeze(['top', 'bottom', 'right', 'left']);
Object.freeze(['primary', 'secondary', 'muted', 'success', 'warning', 'upgrade', 'info']);
Object.freeze(['light', 'dark']);
Object.freeze(['primary', 'light', 'dark', 'transparent']);
Object.freeze(['left', 'right', 'even', 'center']);
Object.freeze(['normal', 'near', 'far']);
Object.freeze(['up', 'right', 'down', 'left']);
Object.freeze(['slow', 'normal', 'fast']);
Object.freeze({
  speed: {
    slow: 300,
    normal: 200,
    fast: 100
  }
});
Object.freeze({
  speed: {
    slow: 450,
    normal: 350,
    fast: 250
  },
  distance: {
    normal: 25,
    near: 5,
    far: 75
  }
});

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

var defineProperty = _defineProperty;

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

const useStyles = createUseStyles(theme => ({
  wdsTypography: ({
    align,
    color,
    variant
  }) => {
    const typeColor = theme.typeColor,
          semanticColor = theme.semanticColor,
          fontFamily = theme.fontFamily,
          fontSize = theme.fontSize,
          fontWeight = theme.fontWeight;
    const chosenColor = typeColor[color] || semanticColor[color];
    return _objectSpread(_objectSpread({
      fontFamily: fontFamily.base,
      color: chosenColor,
      textAlign: align,
      lineHeight: 1.5,
      margin: 0
    }, variant && variant !== 'link' && {
      fontSize: fontSize[variant],
      fontWeight: {
        hero1: fontWeight.light,
        hero2: fontWeight.light,
        title: fontWeight.light,
        sectionTitle: fontWeight.light,
        pageTitle: fontWeight.light,
        cardTitle: fontWeight.medium,
        body: fontWeight.regular,
        bodySm: fontWeight.regular
      }[variant]
    }), variant && variant === 'link' && {
      color: typeColor.link,
      textDecoration: 'none',
      border: 'none',
      background: 'none',
      outline: 'none',
      cursor: 'pointer',
      fontWeight: 'inherit',
      fontSize: 'inherit',
      '&:hover, &:focus': {
        textDecoration: 'underline'
      }
    });
  }
}));

const ALIGNMENT = ['center', 'left', 'right'];
const VARIANTS = ['body', 'bodySm', 'cardTitle', 'hero1', 'hero2', 'link', 'pageTitle', 'sectionTitle']; // @todo: custom components wanted like <Link />??

const COMPONENT_LIST = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'p', 'span', 'div', 'article', 'section'];
const Typography = /*#__PURE__*/forwardRef((_ref, ref) => {
  let align = _ref.align,
      className = _ref.className,
      children = _ref.children,
      color = _ref.color,
      component = _ref.component,
      variant = _ref.variant,
      rest = objectWithoutProperties(_ref, ["align", "className", "children", "color", "component", "variant"]);

  const Component = component;
  const theme = useTheme();
  const classes = useStyles({
    align,
    color,
    variant,
    theme
  });
  const classNames = clsx(className, classes.wdsTypography);
  return /*#__PURE__*/React.createElement(Component, Object.assign({
    className: classNames
  }, rest, {
    ref: ref
  }), children);
});
Typography.propTypes = {
  /**
   * Align content
   */
  align: PropTypes.oneOf(ALIGNMENT),

  /**
   * Text content to be displayed
   */
  children: PropTypes.node.isRequired,
  className: PropTypes.string,

  /**
   * Color variants
   */
  color: PropTypes.oneOf(SEMANTIC_COLORS.concat(TYPE_COLORS)),

  /**
   * Must be one of the following components:
   */
  component: PropTypes.oneOf(COMPONENT_LIST),

  /**
   * Our variants of choice are:
   */
  variant: PropTypes.oneOf(VARIANTS)
};
Typography.defaultProps = {
  align: 'left',
  className: undefined,
  component: 'div',
  color: 'dark',
  variant: 'body'
};
const TypographyWithSpacing = withSpacing(Typography);
TypographyWithSpacing.displayName = 'Typography';
Typography.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "Typography",
  "props": {
    "align": {
      "defaultValue": {
        "value": "'left'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "value": [{
          "value": "'center'",
          "computed": false
        }, {
          "value": "'left'",
          "computed": false
        }, {
          "value": "'right'",
          "computed": false
        }]
      },
      "required": false,
      "description": "Align content"
    },
    "className": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "component": {
      "defaultValue": {
        "value": "'div'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "value": [{
          "value": "'h1'",
          "computed": false
        }, {
          "value": "'h2'",
          "computed": false
        }, {
          "value": "'h3'",
          "computed": false
        }, {
          "value": "'h4'",
          "computed": false
        }, {
          "value": "'h5'",
          "computed": false
        }, {
          "value": "'h6'",
          "computed": false
        }, {
          "value": "'a'",
          "computed": false
        }, {
          "value": "'p'",
          "computed": false
        }, {
          "value": "'span'",
          "computed": false
        }, {
          "value": "'div'",
          "computed": false
        }, {
          "value": "'article'",
          "computed": false
        }, {
          "value": "'section'",
          "computed": false
        }]
      },
      "required": false,
      "description": "Must be one of the following components:"
    },
    "color": {
      "defaultValue": {
        "value": "'dark'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "computed": true,
        "value": "SEMANTIC_COLORS.concat(TYPE_COLORS)"
      },
      "required": false,
      "description": "Color variants"
    },
    "variant": {
      "defaultValue": {
        "value": "'body'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "value": [{
          "value": "'body'",
          "computed": false
        }, {
          "value": "'bodySm'",
          "computed": false
        }, {
          "value": "'cardTitle'",
          "computed": false
        }, {
          "value": "'hero1'",
          "computed": false
        }, {
          "value": "'hero2'",
          "computed": false
        }, {
          "value": "'link'",
          "computed": false
        }, {
          "value": "'pageTitle'",
          "computed": false
        }, {
          "value": "'sectionTitle'",
          "computed": false
        }]
      },
      "required": false,
      "description": "Our variants of choice are:"
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": true,
      "description": "Text content to be displayed"
    }
  }
};

export { TypographyWithSpacing as Typography };
//# sourceMappingURL=index.es.js.map
